import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getRegionMeta } from '../utils/region';
type LiveOrder = {
  material: string;
  quantity: number;
  city: string;
  createdAt: Date;
};
const ORDER_TEMPLATES = [{
  material: '5/8" Minus',
  quantity: 12,
  fallbackCity: 'Tacoma'
}, {
  material: '3-Way Topsoil',
  quantity: 15,
  fallbackCity: 'Kent'
}, {
  material: 'Drain Rock',
  quantity: 10,
  fallbackCity: 'Puyallup'
}, {
  material: 'Medium Bark',
  quantity: 14,
  fallbackCity: 'Bellevue'
}, {
  material: 'Pea Gravel',
  quantity: 8,
  fallbackCity: 'Auburn'
}, {
  material: '5/8" Clear',
  quantity: 11,
  fallbackCity: 'Renton'
}] as const;
type LiveOrderTickerProps = {
  isDispatchActive?: boolean;
  opensIn?: string;
  deliveryMessage?: string;
};
export function LiveOrderTicker({
  isDispatchActive = true,
  opensIn = '',
  deliveryMessage = ''
}: LiveOrderTickerProps) {
  const shouldReduceMotion = useReducedMotion();
  const [now, setNow] = useState(() => new Date());
  const [closedMessageIndex, setClosedMessageIndex] = useState(0);
  const orders = useMemo<LiveOrder[]>(() => {
    const cities = getRegionMeta().cities;
    const created = new Date();
    return ORDER_TEMPLATES.map((order, index) => {
      const createdAt = new Date(created);
      createdAt.setMinutes(createdAt.getMinutes() - (index * 19 + 6));
      return {
        material: order.material,
        quantity: order.quantity,
        city: cities[index % cities.length] || order.fallbackCity,
        createdAt
      };
    });
  }, []);
  useEffect(() => {
    // When dispatch closes, lock the relative time in place so this reads as
    // the last completed feed rather than implying orders are still arriving.
    setNow(new Date());
    if (!isDispatchActive) return;
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive]);
  useEffect(() => {
    if (isDispatchActive || shouldReduceMotion) {
      setClosedMessageIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setClosedMessageIndex(index => (index + 1) % 2);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive, shouldReduceMotion]);
  const marqueeOrders = [...orders, ...orders];
  if (!isDispatchActive) {
    const availabilityMessage = 'Dispatch closed · Order online 24/7';
    const nextDeliveryMessage = deliveryMessage ? `${deliveryMessage.replace('Order online for ', '')} available` : 'Next-day delivery available';
    const closedMessages = [availabilityMessage, nextDeliveryMessage];
    const activeMessage = closedMessages[closedMessageIndex];
    return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label={`${availabilityMessage}. ${nextDeliveryMessage}.`} data-id="element-1130" data-mp="1jtv2u86jxjb">
        <span className="mr-2 flex h-3 shrink-0 items-end gap-0.5" aria-hidden="true" data-id="element-1131" data-mp="1vw9x1ity0pid">
          <span className="h-1.5 w-0.5 rounded-full bg-white/30" data-id="element-1132" data-mp="24d0p8kk093sj" />
          <span className="h-3 w-0.5 rounded-full bg-white/30" data-id="element-1133" data-mp="24csd4uvpi2ie" />
          <span className="h-2 w-0.5 rounded-full bg-white/30" data-id="element-1134" data-mp="28duhtr8e76rr" />
        </span>
        <div className="min-w-0 overflow-hidden" data-id="element-1135" data-mp="2d5m3poglsobk">
          <AnimatePresence initial={false} mode="wait" data-id="element-1136" data-mp="2d64c1jgjiszu">
            <motion.p key={activeMessage} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? undefined : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="truncate whitespace-nowrap text-[11px] font-medium tracking-wide text-white/60" data-id="element-1137" data-mp="2i4yvmnmc0ll0">
              {activeMessage}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>;
  }
  return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label="Recent orders" data-id="element-1138" data-mp="3asl5ueq2eogu">
      <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1139" data-mp="nk5d83qk3x1e">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#00b774]" data-id="element-1140" data-mp="nmi456ealhql">Live</span>
        <span className="flex h-3 items-end gap-0.5" aria-hidden="true" data-id="element-1141" data-mp="inx3x6z1iy25">
          {[0, 1, 2].map(index => <motion.span key={index} className="w-0.5 rounded-full bg-[#00b774]" animate={{
          height: ['35%', '100%', '35%']
        }} transition={{
          duration: 1.15,
          repeat: Infinity,
          delay: index * 0.18,
          ease: 'easeInOut'
        }} data-id="element-1142" data-mp="wiz5jhwuu195" />)}
        </span>
      </div>
      <span className="mr-2 shrink-0 text-white/30" aria-hidden="true" data-id="element-1143" data-mp="1fbtkwcye3vdw">•</span>
      <div className="min-w-0 overflow-hidden" data-id="element-1144" data-mp="1fdxmgo8tgib0">
        <motion.div className="flex w-max items-center whitespace-nowrap" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: Math.max(52, orders.length * 9),
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-1145" data-mp="1b9zmkbbo33gw">
          {marqueeOrders.map((order, index) => <span key={`${order.material}-${index}`} className="inline-flex items-center text-[11px] font-medium text-white/50 sm:text-xs" data-id="element-1146" data-mp="23wzfnnym59c5" data-mpi={index}>
              <span className="text-white/50" data-id="element-1147" data-mp="28uhlv6l8xagg">{order.quantity} yd {order.material}</span>
              <span className="mx-1.5 text-[#00b774]" data-id="element-1148" data-mp="1txicbd6u37jc">→</span>
              <span data-id="element-1149" data-mp="1yxd0h8z85j0p">{order.city}</span>
              <span className="mx-1.5 text-white/35" data-id="element-1150" data-mp="1ywbon2adwshx">·</span>
              <span className="text-white/35" data-id="element-1151" data-mp="1l6i06l2ska8q">{formatRelativeTime(order.createdAt, now)}</span>
              <span className="mx-4 text-white/25" data-id="element-1152" data-mp="1pwyfkaq6a5xq">•</span>
            </span>)}
        </motion.div>
      </div>
    </div>;
}
function formatRelativeTime(createdAt: Date, now: Date): string {
  const minutes = Math.max(1, Math.round((now.getTime() - createdAt.getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)} hr ago`;
}